import { Card, Divider, Pagination, Button, message } from 'antd';
import { useEffect, useState, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { DownloadOutlined } from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import SearchFilter from '../../component/Filter';
import baseuri from '../../methods/baseuri';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import {
	getBlockchainNetworks,
	getCurrencies,
} from '../../methods/redux/reducers/currency';
import { getStakingReport } from '../../methods/redux/reducers/staking';
import { getUserProfileById } from '../../methods/redux/reducers/user-profile';
import remakeQueryString from '../../methods/utils/remake-query-string';
import ColumnStaking from './ColumnStaking';
import StakingAggregate from './StakingAggregate';
import StakingFilter from './StakingFilter';

export const UsersListPage = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	gap: 1em;

	& .myCard {
		border-radius: 10px;
		gap: 1em;
	}
`;
export const MainSection = styled(Card)`
	border-radius: 10px;
	gap: 2em;
`;

const MyButton = styled(Button)`
	color: #383838;
	border-radius: 10px;
	margin: 5px;

	&:hover {
		background-color: #7eb1f5;
		color: white;
	}
	&:focus {
		background-color: blue;
		color: white;
	}

	&.selected {
		background-color: blue;
		color: white;
	}
`;

const DownloadButton = styled(Button)`
	color: red;
	background-color: #f5d5d5;
	border-radius: 5px;
	&:hover {
		color: red;
		background-color: #efe7e7;
	}
	&:focus {
		color: red;
		background-color: #f5d5d5;
	}
`;

const StakingReport = () => {
	const dispatch = useAppDispatch();
	const { userId } = useParams();

	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState([] as any);

	const { user } = useAppSelector((store) => store.userProfile);
	const { stakingReport } = useAppSelector((store) => store.staking);
	const { currencies } = useAppSelector((store) => store.currencies);
	const getQueryParams = () => Object.fromEntries(searchParams.entries());

	const csvLinkRef = useRef<
		CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
	>(null);

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: String(stakingReport.paginator.perPage),
			});
		}

		if (userId) {
			dispatch(getUserProfileById(userId));
		}

		dispatch(getCurrencies({}));
		dispatch(getBlockchainNetworks());
	}, []);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length > 0) {
			if (userId) {
				dispatch(getStakingReport({ ...queryData, userId }));
			} else {
				dispatch(getStakingReport(queryData));
			}
		}
	}, [searchParams]);

	const currencySelect = currencies.map((item: any) => ({
		name: item.symbol,
		value: item._id,
	}));

	const selectedFilter = searchParams.has('status');

	const filterEnum = [
		{ name: 'Staked', value: 'staked' },
		{ name: 'Pending', value: 'pending' },
		{ name: 'Completed', value: 'completed' },
		{ name: 'Deleted', value: 'deleted' },
	];

	const filterAllStaking = (value: any) => {
		const filterText = { status: value };
		updateQueryString({ ...filterText, pageId: '1' });
	};

	// Transform the results data to match the headers
	const transformDownloadRequests = (requests: any[]) =>
		requests?.map((data: any) => {
			const created = moment(data.createdAt);
			const unstake = data.unstakeDate ? moment(data.unstakeDate) : null;

			let runningDays = 0;
			if (data.status === 'staked') {
				runningDays = moment().diff(created, 'days');
			} else if (data.status === 'completed' && unstake) {
				runningDays = unstake.diff(created, 'days');
			}

			const dailyInterest = (data.amount * (data.apr || 0)) / 100 / 365;
			const runningDaysInterest = dailyInterest * runningDays;

			return {
				status: data.status,
				migrateStatus: data.migrateStatus,
				amount: data.amount,
				customer: data.userId?.username || '', // Handle nested value
				stakingProfile: `(${data.apr || 0}%)`, // Handle computed value
				periodInDays: data.periodInDays,
				withdrawalPenaltyApr: data.withdrawalPenaltyApr,
				createdAt: created.format('lll'), // Handle formatted date
				unstakeDate: unstake ? unstake.format('lll') : '',
				runningDays,
				runningDaysInterest,
				dailyInterest,
			};
		});

	// Define headers with simple keys
	const headers = [
		{ label: 'Status', key: 'status' },
		{ label: 'Migration Status', key: 'migrateStatus' },
		{ label: 'Amount', key: 'amount' },
		{ label: 'Customer', key: 'customer' },
		{ label: 'Staking Profile', key: 'stakingProfile' },
		{ label: 'Period In Days', key: 'periodInDays' },
		{ label: 'Withdrawal Penalty Apr', key: 'withdrawalPenaltyApr' },
		{ label: 'Created At', key: 'createdAt' },
		{ label: 'Unstake Date', key: 'unstakeDate' },
		{ label: 'Running Days', key: 'runningDays' },
		{ label: 'Running Days Interest', key: 'runningDaysInterest' },
		{ label: 'Daily Interest', key: 'dailyInterest' },
	];

	const handleDownload = () => {
		setLoading(true);
		const params1 = getQueryParams();
		setResults([]);
		baseuri
			.get('/report/staking/total' || '', {
				params: {
					...params1,
					perPage: stakingReport.paginator.itemCount || 0,
					pageId: '1',
				},
			})
			.then((res: any) => {
				const rawData = res.data.data.itemsList;
				const transformedData = transformDownloadRequests(rawData);
				setResults(transformedData);
				setLoading(false);
			})
			.catch(() => {
				message.error('could not download CSV');
				setLoading(false);
			});
	};

	useEffect(() => {
		if (results.length > 0) {
			csvLinkRef?.current?.link.click();
		}
	}, [results]);

	return (
		<Card>
			<h1>
				Staking Report{' '}
				{userId && (
					<span>
						{' '}
						for{' '}
						<Link
							title="View Profile"
							to={`/user-profile/${user.profile?._id}`}
						>
							@{user.profile?.username}
						</Link>
					</span>
				)}
			</h1>
			{/* <p>All the deposit of funds from an external wallet address.</p> */}

			<StakingFilter />

			<div style={{ marginTop: '16px' }}>
				<p
					style={{ marginBottom: '6px', fontSize: '16px', fontWeight: 'bold' }}
				>
					All staking filters:
				</p>
				<div style={{ display: 'flex', gap: '8px' }}>
					{filterEnum?.length > 0 &&
						filterEnum.map((filter) => (
							<MyButton
								className={
									selectedFilter && searchParams.get('status') === filter.value
										? 'selected'
										: ''
								}
								key={filter.value}
								onClick={() => filterAllStaking(filter.value)}
							>
								{filter.name}
							</MyButton>
						))}
				</div>
			</div>

			<br />
			<StakingAggregate tokens={stakingReport.record} />

			<br />

			<SearchFilter
				title="Search Staking filters"
				searchFilters={[
					{
						label: 'Currency',
						name: 'currencyId',
						htmlElement: 'select',
						selectOptions: currencySelect,
					},
					{
						label: 'Start Date',
						name: 'startDate',
						htmlElement: 'datepicker',
					},
					{
						label: 'End Date',
						name: 'endDate',
						htmlElement: 'datepicker',
					},
				]}
				retrieveQueryString={updateQueryString}
			/>

			<br />

			<p>
				Total of <strong>{stakingReport.paginator.itemCount} items</strong>{' '}
				found
			</p>

			<br />

			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={stakingReport.paginator.itemCount}
				current={stakingReport.paginator.currentPage}
				pageSize={stakingReport.paginator.perPage}
			/>

			{stakingReport?.itemsList?.length > 0 && (
				<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
					<DownloadButton
						danger
						loading={loading}
						icon={<DownloadOutlined />}
						onClick={() => handleDownload()}
					>
						{loading ? 'Loading csv...' : 'Download CSV'}
					</DownloadButton>
					<CSVLink
						data={results}
						headers={headers}
						className="download"
						filename="StakingReport.csv"
						ref={csvLinkRef}
					/>
				</div>
			)}
			<br />

			{stakingReport.itemsList.map((item: any) => (
				<ColumnStaking key={item?._id} data={item} />
			))}

			<Divider />

			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={stakingReport.paginator.itemCount}
				current={stakingReport.paginator.currentPage}
				pageSize={stakingReport.paginator.perPage}
				showSizeChanger
				showQuickJumper
			/>
		</Card>
	);
};

export default StakingReport;
